import { IconButton, Tooltip } from '@mui/material';
import cn from 'classnames';
import { observer } from 'mobx-react-lite';
import { NavLink } from 'react-router-dom';

import { Collapsed } from '@/components/Collapsed';
import { useTrackerStore } from '@/tracker/model';
import { ExpandIcon } from '@/tracker/ui/ExpandIcon';

import s from '../navItem.module.scss';

export type NavFavorite = { id: string; label: string; to: string };

type Props = {
  text: string;
  icon: React.ReactNode;
  link: string;
  end: boolean;
  isCollapsed: boolean;
  /** Избранное раздела; null — раздел не раскрывается */
  favorites: NavFavorite[] | null;
};

/**
 * Раздел навигации. Сам пункт ведёт в каталог («все»), под ним раскрывается избранное —
 * личные точки входа, как в панели Яндекс Трекера
 */
const NavSection: React.FC<Props> = observer(
  ({ text, icon, link, end, isCollapsed, favorites }) => {
    const trackerStore = useTrackerStore();
    const isOpen = trackerStore.isNavSectionOpen(link);
    const isExpandable = !!favorites && !isCollapsed;

    return (
      <>
        <Tooltip title={isCollapsed ? text : ''} placement="right">
          {/* Обёртка: Tooltip перетирает className-функцию NavLink */}
          <div className={s.nav__row}>
            <NavLink
              to={link}
              end={end}
              className={({ isActive }) => cn(s.nav__link, isActive && s.nav__link_active)}
              aria-label={text}
            >
              {icon}
              {!isCollapsed && <span className={s.nav__label}>{text}</span>}
            </NavLink>
            {isExpandable && (
              <IconButton
                size="small"
                aria-label={`Избранное: ${text}`}
                aria-expanded={isOpen}
                onClick={() => trackerStore.toggleNavSection(link)}
              >
                <ExpandIcon isOpen={isOpen} />
              </IconButton>
            )}
          </div>
        </Tooltip>

        {isExpandable && (
          <Collapsed opened={isOpen}>
            <div className={s.nav__favorites} role="group" aria-label={`Избранное: ${text}`}>
              {favorites.map((favorite) => (
                <NavLink
                  key={favorite.id}
                  to={favorite.to}
                  className={({ isActive }) =>
                    cn(s.nav__link, s.nav__link_favorite, isActive && s.nav__link_active)
                  }
                  title={favorite.label}
                >
                  <span className={s.nav__label}>{favorite.label}</span>
                </NavLink>
              ))}
              {!favorites.length && (
                <span className={s.nav__hint}>Отметьте звёздочкой в шапке — появится здесь</span>
              )}
            </div>
          </Collapsed>
        )}
      </>
    );
  },
);

export { NavSection };
